import React, { useState, useEffect, useMemo, memo } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  MenuBook as GuideIcon,
} from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import MarkdownContent from '../../../shared/components/MarkdownContent';
import { EntityListPanel, EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import MasterDetailLayout from '../../../shared/components/MasterDetailLayout';
import { useViewport } from '../../../shared/hooks/useViewport';
import { useHelpGuides, useHelpGuideContent, type Guide } from '../hooks/useHelpGuides';

interface HelpModalProps {
  open: boolean;
  onClose: () => void;
}

const HelpModal: React.FC<HelpModalProps> = ({ open, onClose }) => {
  const { isCompact } = useViewport();
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);
  const [errorDismissed, setErrorDismissed] = useState(false);

  const guidesQuery = useHelpGuides(open);
  const guides = useMemo(() => guidesQuery.data ?? [], [guidesQuery.data]);
  const contentQuery = useHelpGuideContent(selectedGuide?.key);

  // Auto-select the first guide once the list loads. Not below `md`: there the
  // article replaces the list, so the reader would never see the guides to pick from.
  useEffect(() => {
    if (!isCompact && guides.length > 0 && !selectedGuide) {
      setSelectedGuide(guides[0]);
    }
  }, [isCompact, guides, selectedGuide]);

  const queryError = guidesQuery.error || contentQuery.error;
  useEffect(() => {
    if (queryError) setErrorDismissed(false);
  }, [queryError]);

  const error = queryError && !errorDismissed
    ? (guidesQuery.error
        ? 'Failed to load documentation guides. Please try again.'
        : 'Failed to load guide content. Please try again.')
    : null;

  const guideContent = contentQuery.data ?? '';
  const loading = contentQuery.isLoading;
  const guidesLoading = guidesQuery.isLoading;

  const guideListConfig = useMemo<EntityListItemConfig<Guide>>(() => ({
    getPrimaryText: (g) => g.title,
    getSecondaryText: (g) => g.description,
    getId: (g) => g.key,
    secondaryTypographyProps: { variant: 'caption' as const, noWrap: false },
    getSearchText: (g) => `${g.title} ${g.description}`,
  }), []);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isCompact}
      PaperProps={{
        sx: isCompact ? undefined : { height: '90vh', maxHeight: 900 }
      }}
    >
      <ModalHeader icon={GuideIcon} title="Help & Documentation" onClose={handleClose} />

      <DialogContent dividers sx={{ p: 0, display: 'flex', height: '100%' }}>
        <MasterDetailLayout
          hasSelection={selectedGuide !== null}
          onBack={() => setSelectedGuide(null)}
          listWidth={280}
          backLabel="Back to guides"
          list={
            <EntityListPanel
              entities={guides}
              selectedEntity={selectedGuide}
              isNew={false}
              saving={false}
              config={guideListConfig}
              emptyStateText="No guides match your search"
              emptyStateHelperText=""
              onSelect={setSelectedGuide}
              isLoading={guidesLoading}
              searchable
              headerContent={
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Guides</Typography>
                  {/* Below `md` the detail pane is hidden until a guide is picked, so a
                      failed list load is said above the list, where the reader is. */}
                  {isCompact && error && !selectedGuide && (
                    <InlineAlert severity="error" onClose={() => setErrorDismissed(true)} sx={{ mb: 1 }}>
                      {error}
                    </InlineAlert>
                  )}
                </>
              }
            />
          }
          detail={loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 3 }}>
              <InlineAlert severity="error" onClose={() => setErrorDismissed(true)}>
                {error}
              </InlineAlert>
            </Box>
          ) : guideContent ? (
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <MarkdownContent content={guideContent} variant="document" syntaxHighlight />
            </Box>
          ) : (
            <EmptyStateMessage
              icon={GuideIcon}
              title="Select a guide"
              description="Choose a guide from the list to get started"
            />
          )}
        />
      </DialogContent>

      <ModalFooter count={guides.length} label="guides available" onClose={handleClose} />
    </Dialog>
  );
};

export default memo(HelpModal);
